import { LoadingButton } from '@mui/lab';
import { Alert, alpha, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, MenuItem, Popover, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar, useBoolean, useResponsive, useUserDetailsStore } from '@zeta-core';
import { EditPencilSvgIcon, PlusIcon, TrashBinErrorFilled, VerticalDotIcon } from '@zeta-core/assets';
import { WPDocumentQA } from '@zeta-services';
import { IUpdateSessionNameRequest } from '@zeta-types';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';

import { DASHBOARD_CONFIG } from '../fact-find/common/constants';
import ErrorDialog from '../fact-find/components/error-dialog';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

/* =========================================================
   HELPERS (UNCHANGED)
========================================================= */
export function scrollToBottomById(elementId: string) {
  const element = document.getElementById(elementId);
  if (element) element.scrollTop = element.scrollHeight;
}

export const categorizeData = (data = []) => {
  const categories = [
    { label: 'Today', data: [] },
    { label: 'Last 7 Days', data: [] },
    { label: 'Last 30 Days', data: [] },
    { label: 'Last Year', data: [] },
    { label: 'Last Decade', data: [] },
  ];

  const now = dayjs().tz('Europe/London');
  data?.forEach?.((item) => {
    const updatedAt = dayjs(item.updated_at).tz('Europe/London');
    if (updatedAt.isToday()) categories[0].data.push(item);
    else if (now.diff(updatedAt, 'day') <= 7) categories[1].data.push(item);
    else if (now.diff(updatedAt, 'day') <= 30) categories[2].data.push(item);
    else if (now.diff(updatedAt, 'year') <= 1) categories[3].data.push(item);
    else if (now.diff(updatedAt, 'year') <= 10) categories[4].data.push(item);
  });

  return categories;
};

/* =========================================================
   SIDEPANEL
========================================================= */
const Sidepanel = ({
  selectedSession,
  setSelectedSession,
  sessions,
  getSessions,
  setSessionId,
  setInput,





















  
  setConversations,
  isReadOnly,
  handlecreateNew,
  handleSessionClick,
}: any) => {
  const theme = useTheme();
  const { id } = useUserDetailsStore((state) => state.user);
  const lgUp = useResponsive('up', 'lg');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [current, setCurrent] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [modal, setModal] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string>('');

  const isError = useBoolean(false);
  const isLoading = useBoolean(false);
  const categorizedData = categorizeData(sessions);

  /* ===============================
     EFFECTS (UNCHANGED)
  =============================== */
  useEffect(() => {
    if (editingId && inputValue && selectedSession?.id == editingId) {
      const session = sessions.find((e) => e.id == editingId);
      if (session) handleSessionClick(session);
    }
    setEditingId('');
    setInputValue('');
  }, [sessions]);

  /* ===============================
     MUTATIONS (UNCHANGED)
  =============================== */
  const updateSessionName = useMutation({
    mutationFn: (payload: IUpdateSessionNameRequest) =>
      WPDocumentQA().updateChatSessionName(payload),
    onSuccess: () => {
      getSessions.mutateAsync();
      enqueueSnackbar('Topic renamed successfully', { variant: 'success' });
    },
    onError: () => {
      setEditingId('');
      setInputValue('');
      isError.onTrue();
      setTimeout(isError.onFalse, 3000);
    },
  });

  const deleteSession = useMutation({
    mutationFn: (session_id: string) =>
      WPDocumentQA().deleteSession(session_id),
    onSuccess: () => {
      setModal(false);
      getSessions.mutateAsync();
      if (deleteId == selectedSession.id) {
        setConversations?.([]);
        setSelectedSession?.({});
        setSessionId?.('');
        setInput('');
      }
      setDeleteId('');
      enqueueSnackbar('Topic deleted successfully', { variant: 'success' });
    },
    onError: () => {
      setModal(false);
      isError.onTrue();
      setTimeout(isError.onFalse, 3000);
    },
  });

  /* ===============================
     HANDLERS (UNCHANGED)
  =============================== */
  const handleActionClick = (event: React.MouseEvent<HTMLDivElement>, id: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setCurrent(id);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setCurrent(null);
  };

  const handleRename = (id: string, session_name: string) => {
    setEditingId(id);
    setInputValue(session_name);
    handleClosePopover();
  };

  const handleSave = (id: string) => {
    if (id && inputValue) {
      updateSessionName.mutateAsync({ session_id: id, session_name: inputValue });
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setModal(true);
    handleClosePopover();
  };

  /* ===============================
     RENDER
  =============================== */
  return (
    <>
      <Stack
        sx={{
          mt: lgUp ? DASHBOARD_CONFIG.HEADER.DESKTOP_HEIGHT / 8 : 0,
          maxWidth: DASHBOARD_CONFIG.NAVIGATION.DESKTOP_WIDTH,
          width: '100%',
          position: 'fixed',
          height: '100%',
          left: 0,
          top: 0,
          bgcolor: theme.palette.grey[100],
          borderRight: `1px solid ${theme.palette.grey[300]}`,
          p: 3,
          gap: 3,
          zIndex: theme.zIndex.appBar + 3,
          overflowY: 'auto',
        }}
      >
        {/* HEADER */}
        <Stack spacing={1}>
          <Typography variant="h6">
            Compliance Document Review
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Create and manage compliance topics to organise, review, and assess
            regulatory and advice-related documents.
          </Typography>
        </Stack>

        {/* COMPLIANCE NOTE */}
        <Alert severity="info" sx={{ fontSize: 12, borderRadius: 2 }}>
          This area is designed to support compliance review and preparation.
          All outputs must be reviewed and approved by a suitably authorised
          adviser before being relied upon for regulated advice.
        </Alert>

        {!isReadOnly && (
          <Button
            startIcon={<PlusIcon />}
            disabled={isLoading.value}
            onClick={() => setCreateDialogOpen(true)}
            variant="contained"
          >
            Create Compliance Topic
          </Button>
        )}

        {/* SESSION LIST */}
        {sessions?.length > 0 &&
          categorizedData.map((sessionsList) =>
            sessionsList.data.length ? (
              <List disablePadding key={sessionsList.label}>
                <ListSubheader
                  sx={{ background: 'inherit', px: 1.5, lineHeight: '18px' }}
                >
                  {sessionsList.label}
                </ListSubheader>

                {sessionsList.data
                  .sort(
                    (a, b) =>
                      dayjs(b?.updated_at).valueOf() -
                      dayjs(a?.updated_at).valueOf()
                  )
                  .map((item) => {
                    const sessionid = item?.id || item?.session_id;
                    const chatName =
                      item?.chat_name || item?.session_name;

                    return (
                      <Box key={sessionid}>
                        {editingId == sessionid ? (
                          <TextField
                            value={inputValue}
                            fullWidth
                            size="small"
                            sx={{
                              mb: 1,
                              bgcolor: theme.palette.background.neutral,
                              borderRadius: 2,
                            }}
                            onChange={(e) => setInputValue(e.target.value)}
                            onBlur={() => handleSave(sessionid)}
                          />
                        ) : (
                          <ListItem disablePadding sx={{ mb: 1 }}>
                            <ListItemButton
                              onClick={() => handleSessionClick(item)}
                              sx={{
                                borderRadius: 1,
                                bgcolor:
                                  item?.session_id ===
                                  selectedSession?.session_id
                                    ? alpha(
                                        theme.palette.primary.main,
                                        0.14
                                      )
                                    : undefined,
                              }}
                            >
                              <ListItemText>
                                <Typography
                                  variant="subtitle2"
                                  noWrap
                                >
                                  {chatName?.replace(/"/g, '')}
                                </Typography>
                              </ListItemText>

                              <ListItemIcon
                                onClick={(e) =>
                                  handleActionClick(e, sessionid)
                                }
                              >
                                <VerticalDotIcon />
                              </ListItemIcon>
                            </ListItemButton>
                          </ListItem>
                        )}

                        <Popover
                          open={current == sessionid}
                          anchorEl={anchorEl}
                          onClose={handleClosePopover}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                        >
                          <MenuItem
                            onClick={() =>
                              handleRename(sessionid, chatName)
                            }
                          >
                            <EditPencilSvgIcon />
                            <Typography ml={1}>Rename</Typography>
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleDelete(sessionid)}
                            sx={{ color: 'error.main' }}
                          >
                            <TrashBinErrorFilled />
                            <Typography ml={1}>Delete</Typography>
                          </MenuItem>
                        </Popover>
                      </Box>
                    );
                  })}
              </List>
            ) : null
          )}
      </Stack>

      {/* DELETE CONFIRM */}
      <Dialog open={modal} fullWidth maxWidth="sm">
        <DialogTitle>Please confirm</DialogTitle>
        <DialogContent>
          <Typography>
            This compliance topic and all associated review history will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModal(false)} variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={() => deleteSession.mutateAsync(deleteId)}
            loading={deleteSession.isPending}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Compliance Topic</DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Provide a clear name for this compliance topic. This will help you
              organise documents and review activity (for example, “Consumer Duty – File Review”).
            </Typography>

            <TextField
              label="Compliance Topic Name"
              placeholder="e.g. Consumer Duty – File Review"
              value={newTopicName}
              onChange={(e) => setNewTopicName(e.target.value)}
              fullWidth
              required
              autoFocus
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setCreateDialogOpen(false);
              setNewTopicName('');
            }}
          >
            Cancel
          </Button>

          <LoadingButton
            variant="contained"
            disabled={!newTopicName.trim()}
            onClick={() => {
              handlecreateNew(newTopicName.trim());
              setCreateDialogOpen(false);
              setNewTopicName('');
            }}
          >
            Create Topic
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ErrorDialog open={isError.value} />
    </>
  );
};

export default Sidepanel;
